// Prepara lo que Chrome va oyendo antes de buscar el comando (partida y repeticion):
// 1) palabras parecidas -> la palabra del vocabulario (isquierda -> izquierda, sinco -> cinco);
// 2) solo cuenta lo ultimo: desde la ultima palabra clave (mueve, incorpora, captura,
//    domina, Xima) o, si no hay, las ultimas palabras que aun no se usaron.

export const VOICE_COMMAND_WINDOW_WORDS = 6;

// Variantes conocidas de como escribe Chrome (ya sin tildes y en minusculas).
const KNOWN_VARIANTS = {
  isquierda: "izquierda", izquieda: "izquierda", izquierd: "izquierda", quierda: "izquierda",
  isquierdo: "izquierdo", izquierdo: "izquierdo",
  derech: "derecha", dereche: "derecha", derecho: "derecho",
  siguente: "siguiente", sigiente: "siguiente", siguient: "siguiente", siguiete: "siguiente",
  atra: "atras", atraz: "atras",
  pauza: "pausa", plei: "play", plai: "play", pley: "play",
  sinco: "cinco", cinko: "cinco", sei: "seis", seys: "seis", sies: "seis", siete: "siete", sete: "siete",
  dose: "doce", trese: "trece", onse: "once", dies: "diez", nuebe: "nueve", ocho: "ocho",
  ache: "hache", hachee: "hache", je: "ge", ca: "ka", ese: "ese", efee: "efe",
  muevete: "mueve", mueva: "mueve", muevan: "mueve", mover: "mueve", muevo: "mueve", move: "mueve", muebe: "mueve",
  incorpore: "incorpora", incorporar: "incorpora", incorporo: "incorpora", corpora: "incorpora",
  incorporen: "incorpora", incorporalo: "incorpora", incoorpora: "incorpora", incoopora: "incorpora",
  capturar: "captura", capture: "captura", capturo: "captura", captur: "captura", capturen: "captura",
  dominar: "domina", domine: "domina", domino: "domina", dominen: "domina",
  cima: "xima", sima: "xima", chima: "xima", zima: "xima", xima: "xima", jima: "xima",
};

// Palabras de comando para el parecido (solo palabras largas: las cortas son demasiado comunes).
const FUZZY_VOCABULARY = [
  "siguiente", "anterior", "reproducir", "reiniciar", "izquierda", "derecha", "acercar", "alejar",
  "esquina", "incorpora", "captura", "especial", "velocidad", "aumenta", "disminuye", "analiza",
  "analizar", "restablecer", "deshacer", "continuar", "comandos", "microfono", "amarillo",
];
const FUZZY_STOPWORDS = new Set(["jugado", "jugados", "anteriores", "esquinas", "continua"]);

const editDistance = (left, right) => {
  const previous = Array.from({ length: right.length + 1 }, (_, index) => index);
  for (let row = 1; row <= left.length; row += 1) {
    let diagonal = previous[0];
    previous[0] = row;
    for (let column = 1; column <= right.length; column += 1) {
      const saved = previous[column];
      previous[column] = Math.min(
        previous[column] + 1,
        previous[column - 1] + 1,
        diagonal + (left[row - 1] === right[column - 1] ? 0 : 1)
      );
      diagonal = saved;
    }
  }
  return previous[right.length];
};

const fuzzyWord = (word) => {
  if (KNOWN_VARIANTS[word]) return KNOWN_VARIANTS[word];
  if (word.length < 6 || FUZZY_STOPWORDS.has(word) || /\d/.test(word)) return word;
  const limit = word.length >= 9 ? 2 : 1;
  let best = word;
  let bestDistance = limit + 1;
  FUZZY_VOCABULARY.forEach((candidate) => {
    if (Math.abs(candidate.length - word.length) > limit) return;
    const distance = editDistance(word, candidate);
    if (distance < bestDistance) {
      best = candidate;
      bestDistance = distance;
    }
  });
  return bestDistance <= limit ? best : word;
};

const isNumberWord = (word) =>
  /^(?:\d+|uno|un|dos|tres|cuatro|cinco|seis|siete|ocho|nueve|diez|once|doce|trece)$/.test(word || "");
const isFileLetter = (word) =>
  /^(?:[a-m]|be|ve|ce|de|efe|ge|hache|jota|ka|ele|eme)$/.test(word || "");

// «se» junto a una casilla: delante de un numero es la letra C; detras de una letra es 6.
const fixCellWords = (words) =>
  words.map((word, index) => {
    if (word !== "se") return word;
    if (isNumberWord(words[index + 1])) return "ce";
    if (isFileLetter(words[index - 1])) return "seis";
    return word;
  });

export const canonicalVoiceWords = (normalizedText) =>
  fixCellWords(
    String(normalizedText || "")
      .split(" ")
      .filter(Boolean)
      .map(fuzzyWord)
  );

const ANCHORS = new Set(["mueve", "incorpora", "captura", "domina", "xima", "desde"]);

/**
 * De las palabras de un resultado, saca el trozo donde buscar el comando.
 * consumedWords: palabras de ese resultado ya usadas por un comando anterior.
 * Devuelve { text, anchor } o null si no queda nada.
 */
export const extractVoiceCommandSegment = (words, consumedWords = 0) => {
  const tail = words.slice(Math.min(Math.max(0, consumedWords), words.length));
  if (!tail.length) return null;

  let anchorIndex = -1;
  for (let index = tail.length - 1; index >= 0; index -= 1) {
    if (ANCHORS.has(tail[index])) {
      anchorIndex = index;
      break;
    }
  }

  if (anchorIndex >= 0) {
    const anchor = tail[anchorIndex];
    const rest = tail.slice(anchorIndex + 1);
    // «captura G4» y «domina G4» = mover un bloque a G4 (pregunta desde donde si hay varios).
    if (anchor === "captura" || anchor === "domina") {
      return { text: ["mueve", ...rest].join(" "), anchor };
    }
    if (anchor === "xima") return rest.length ? { text: rest.join(" "), anchor } : null;
    return { text: tail.slice(anchorIndex).join(" "), anchor };
  }

  return { text: tail.slice(-VOICE_COMMAND_WINDOW_WORDS).join(" "), anchor: "" };
};
